import React, { useState, useEffect } from "react";
import { Button, Form, Input, Modal, Select, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { CustomIcons } from "@/assets/index";
import { modelTitle } from "@/styles/ModalStyles";

const { Option } = Select;

interface PhysicalButtonModalProps {
  action: "add" | "edit";
  handleAddButton: (values: IAddPhysicalButton) => Promise<void>;
  handleEditButton?: (values: any) => Promise<void>; // Using any since we map between different interfaces
  onClose: () => void;
  isOpen: boolean;
  isLoading: boolean;
  existingData?: any; // Data for editing
}

const PhysicalButtonModal: React.FC<PhysicalButtonModalProps> = ({
  action,
  handleAddButton,
  handleEditButton,
  onClose,
  isOpen,
  isLoading,
  existingData,
}) => {
  const [form] = Form.useForm();

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: IAddPhysicalButton) => {
    try {
      // Prepend +27 to the phone number if it's not already there
      const phoneNumberField =
        action === "edit" ? "phoneNumber" : "buttonPhoneNumber";
      const phoneNumber = values[phoneNumberField as keyof IAddPhysicalButton];

      if (phoneNumber) {
        const phoneStr = String(phoneNumber);
        if (!phoneStr.startsWith("+27")) {
          (values as any)[phoneNumberField] = `+27${phoneStr}`;
        }
      }

      if (action === "edit" && handleEditButton) {
        await handleEditButton(values);
        message.success("Physical button updated successfully!");
      } else {
        await handleAddButton(values);
        message.success("Physical button linked successfully!");
      }
      handleClose();
    } catch (error: any) {
      console.error("Error with physical button:", error);

      // Handle different status codes
      const statusCode = error?.response?.status;
      const errorMessage = error?.response?.data?.message || error?.message;

      switch (statusCode) {
        case 400:
          message.error(
            `Invalid data: ${errorMessage || "Please check your input values"}`
          );
          break;
        case 401:
          message.error("Unauthorized: Please log in again");
          break;
        case 403:
          message.error(
            "Forbidden: You don't have permission to perform this action"
          );
          break;
        case 404:
          message.error("Resource not found: Please try again");
          break;
        case 409:
          message.error(
            `Conflict: ${
              errorMessage ||
              "This device may already be linked to another account"
            }`
          );
          break;
        case 422:
          message.error(
            `Validation error: ${
              errorMessage || "Please check your input values"
            }`
          );
          break;
        case 500:
          message.error("Server error: Please try again later");
          break;
        default:
          message.error(
            `Error ${
              action === "edit" ? "updating" : "linking"
            } physical button: ${errorMessage || "Please try again"}`
          );
      }
    }
  };

  // Pre-populate form when editing
  useEffect(() => {
    if (action === "edit" && existingData && isOpen) {
      // Strip +27 from phone number for display in the input field
      let phoneNumber =
        existingData.buttonPhoneNumber || existingData.phoneNumber;
      if (phoneNumber && phoneNumber.startsWith("+27")) {
        phoneNumber = phoneNumber.substring(3); // Remove the +27 prefix
      }

      form.setFieldsValue({
        name: existingData.name,
        physicalButtonTypeId: existingData.physicalButtonTypeId?.toString(),
        deviceId: existingData.deviceId,
        phoneNumber, // Use the stripped phoneNumber
      });
    } else if (action === "add" && isOpen) {
      form.resetFields();
    }
  }, [action, existingData, isOpen, form]);

  return (
    <Modal
      centered
      open={isOpen}
      onCancel={handleClose}
      width={550}
      closeIcon={
        <CustomIcons.CloseIcon
          width={30}
          height={30}
          viewBox={null}
          svgColor="#1C274C"
        />
      }
      footer={null}
    >
      <div className="flex w-12 py-3 justify-content-start" style={modelTitle}>
        {action === "edit" ? "Edit Physical Button" : "Link Physical Button"}
      </div>

      <div className="mb-4 text-gray-600">
        {action === "edit"
          ? "Update your physical panic button details here."
          : "Manage your physical panic button here. You can add new buttons or delink existing ones from your profile"}
      </div>

      <Form
        form={form}
        name="physicalButtonForm"
        onFinish={onFinish}
        layout="vertical"
        className="w-full"
      >
        <div className="grid">
          {/* Device Name */}
          <div className="col-12 md:col-6">
            <Form.Item
              name="name"
              label="Device Name"
              rules={[
                {
                  required: true,
                  message: "Please enter device name!",
                },
                {
                  min: 2,
                  message: "Device name must be at least 2 characters!",
                },
              ]}
              className="mb-3"
            >
              <Input
                size="large"
                placeholder="Enter device name"
                className="w-full"
              />
            </Form.Item>
          </div>

          {/* Device Type */}
          <div className="col-12 md:col-6">
            <Form.Item
              name="physicalButtonTypeId"
              label="Device Type"
              rules={[
                {
                  required: true,
                  message: "Please select device type!",
                },
              ]}
              className="mb-3"
            >
              <Select
                size="large"
                placeholder="Device type"
                className="w-full"
                disabled={action === "edit"}
              >
                <Option value="1">VISIO</Option>
                <Option value="2">Safer City Button</Option>
              </Select>
            </Form.Item>
          </div>

          {/* Device ID */}
          <div className="col-12 md:col-6">
            <Form.Item
              name="deviceId"
              label="Device ID"
              rules={[
                {
                  required: true,
                  message: "Please enter device ID!",
                },
                {
                  min: 3,
                  message: "Device ID must be at least 3 characters!",
                },
              ]}
              className="mb-3"
            >
              <Input
                size="large"
                placeholder="Enter device ID"
                className="w-full"
                disabled={action === "edit"}
              />
            </Form.Item>
          </div>

          {/* Contact Number */}
          <div className="col-12 md:col-6">
            <Form.Item
              name={action === "edit" ? "phoneNumber" : "buttonPhoneNumber"}
              label="Button Number"
              rules={[
                {
                  required: true,
                  message: "Please enter button number!",
                },
                {
                  pattern: /^\d{9,15}$/,
                  message:
                    "Button number must be 9-15 digits (e.g., 9602005641140)",
                },
              ]}
              className="mb-3"
            >
              <Input
                size="large"
                placeholder="9602005641140"
                className="w-full"
                maxLength={15}
                addonBefore={
                  <div className="flex items-center gap-2 px-2">
                    <span>+27</span>
                  </div>
                }
              />
            </Form.Item>
          </div>
        </div>

        {/* Footer Buttons */}
        <Form.Item className="w-full pt-4 mb-0">
          <div className="flex w-full gap-3 justify-end">
            <Button
              type="primary"
              ghost
              onClick={handleClose}
              size="large"
              className="px-8"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className="px-8 bg-[#1757a2]"
              loading={isLoading}
            >
              {action === "edit" ? "Update" : "Save"}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PhysicalButtonModal;
